import type { FriendInvite, FriendRef } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { da } from '../../i18n/da';
import type { RoomFriends } from '../rooms/useRoomConnection';
import { answerFriendRequest, dismissInvite } from './friends-api';
import { friendUrl } from './friend-where';
import styles from './FriendNotices.module.css';

type Notice =
  | { kind: 'request'; from: FriendRef }
  | { kind: 'invite'; from: FriendRef; invite: FriendInvite }
  | { kind: 'knock'; from: FriendRef };

const keyOf = (notice: Notice) => `${notice.kind}:${notice.from.id}`;

/**
 * Cards over the game for what friends do right now (ADR 0019): someone wants to be friends,
 * a friend calls the player over, or a friend knocks on this room with a code.
 */
export function FriendNotices({
  friends,
  onChanged,
}: {
  friends: RoomFriends;
  /** Something changed in the friends list. */
  onChanged: () => void;
}) {
  const [notices, setNotices] = useState<Notice[]>([]);

  useEffect(
    () =>
      friends.onPush((push) => {
        const add = (notice: Notice) =>
          setNotices((current) => [
            ...current.filter((other) => keyOf(other) !== keyOf(notice)),
            notice,
          ]);
        switch (push.type) {
          case 'request':
            add({ kind: 'request', from: push.from });
            onChanged();
            break;
          case 'invite':
            add({ kind: 'invite', from: push.invite.from, invite: push.invite });
            break;
          case 'knock':
            add({ kind: 'knock', from: push.from });
            break;
          case 'knockGone':
            setNotices((current) =>
              current.filter((notice) => keyOf(notice) !== `knock:${push.playerId}`),
            );
            break;
          case 'changed':
            onChanged();
            break;
        }
      }),
    [friends, onChanged],
  );

  if (notices.length === 0) return null;
  const close = (notice: Notice) =>
    setNotices((current) => current.filter((other) => keyOf(other) !== keyOf(notice)));

  const answer = (notice: Notice, yes: boolean) => {
    close(notice);
    switch (notice.kind) {
      case 'request':
        void answerFriendRequest(notice.from.id, yes).then(onChanged);
        break;
      case 'invite':
        if (yes) window.location.assign(friendUrl(notice.invite.gameId, notice.from.id));
        else void dismissInvite(notice.from.id);
        break;
      case 'knock':
        friends.answerKnock(notice.from.id, yes);
        break;
    }
  };

  return (
    <ul className={styles.notices} aria-live="polite" data-testid="friend-notices">
      {notices.map((notice) => (
        <li key={keyOf(notice)} className={styles.notice}>
          <span>{text(notice)}</span>
          <span className={styles.buttons}>
            <button type="button" className={styles.yes} onClick={() => answer(notice, true)}>
              {notice.kind === 'request'
                ? da.friends.yes
                : notice.kind === 'invite'
                  ? da.friends.goWith
                  : da.friends.letIn}
            </button>
            <button type="button" className={styles.no} onClick={() => answer(notice, false)}>
              {notice.kind === 'knock' ? da.friends.notNow : da.friends.noThanks}
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

function text(notice: Notice): string {
  const name = notice.from.displayName;
  switch (notice.kind) {
    case 'request':
      return da.friends.requestFrom(name);
    case 'invite':
      return da.friends.inviteFrom(name);
    case 'knock':
      return da.friends.knockFrom(name);
  }
}
